import {
	Combobox,
	Option,
	FluentProvider,
	teamsLightTheme,
	Text,
	Divider,
} from "@fluentui/react-components";
import { useIntl } from "react-intl";
import { Flex } from "@/components/atoms/flex";
import { useComponentStyle } from "./style";
import {
	SingleSendEmail,
	SingleSendEmailState,
	useSingleSendEmailStore,
} from "@/common/stores/nonpersistingStores/singleSendEmail";
import {
	EmailData,
	SingleSendEmailEditor,
} from "@/components/molecules/emailEditor";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
	DEFAULT_TRANSCRIPTION_LANGUAGE,
	fetchFilledVoiceTemplateById,
	fetchTranscriptionLanguages,
	singleSend,
	TranscriptionLanguagesKey,
	updateTranscriptionCall,
} from "@/repository/queryFns";
import { addBreadcrumb, captureError } from "@/common/helpers/sentryHelper";
import { SendType } from "@/common/stores/nonpersistingStores/currentInteraction";
import { useEmailInteraction } from "../emailInteractionContent/hook";
import { useMessage } from "@/common/messages";
import {
	CallSessionState,
	useCallSessionStore,
} from "@/common/stores/nonpersistingStores/callSession";
import { HtmlIds } from "@/common/constants";
import { MessagingChatInteractionContent } from "../messagingInteractionContent";
import { InteractionType } from "@/common/types";
import { PlatformType } from "@/common/constants/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AgentInteractionStatus } from "@/common/constants/callSession";

export const CallInteractionContent = () => {
	const intl = useIntl();

	const classes = useComponentStyle();
	const { show: showMessage } = useMessage();

	const methods = useEmailInteraction();

	const callSession = useCallSessionStore(
		(state: CallSessionState) => state.callSession
	);

	const singleSendEmail = useSingleSendEmailStore(
		(state: SingleSendEmailState) => state.singleSendEmail
	);

	const [emailData, setEmailData] = useState<EmailData>();
	const [singleSendInteractionId, setSingleSendInteractionId] = useState<string | undefined>();

	const [transcriptionStarted, setTranscriptionStarted] =
		useState<boolean>(false);

	const [selectedLanguageKey, setSelectedLanguageKey] = useState<
		string | undefined
	>(undefined);

	const showWorkspaceMessage = useCallback(
		(translationId: string, intent: "error" | "success" | "warning" | "info") => {
			showMessage("workspace", {
				text: intl.formatMessage({ id: translationId }),
				intent,
			});
		},
		[intl, showMessage]
	);

	const loadData = async (singleEditEmail: SingleSendEmail) => {
		fetchFilledVoiceTemplateById(
			singleEditEmail.template.templateId,
			singleEditEmail.interactionId,
			singleEditEmail.recipient.name
		)
			.then((result: string) => {
				let emailData = new EmailData();
				emailData.subject = singleEditEmail.template?.templateName ?? "";
				emailData.to = [{ ...singleEditEmail.recipient!, isValid: true }];
				setEmailData({ ...emailData, body: result });
			})
			.catch((_err: any) => {
				showWorkspaceMessage("Contacts.ErrorGettingTemplate", "error");
			});
	};

	useEffect(() => {
		if (singleSendEmail) {
			setSingleSendInteractionId(singleSendEmail.interactionId);
			loadData(singleSendEmail);
		}
	}, [singleSendEmail]);

	useEffect(() => {
		setTranscriptionStarted(callSession?.transcriptionStarted || false);
		setSelectedLanguageKey(
			callSession?.transcriptionLanguage || DEFAULT_TRANSCRIPTION_LANGUAGE
		);
	}, [callSession]);

	const onSendEmailClick = useCallback(async () => {
		addBreadcrumb("ui_interaction", "Sending email", {
			SendType,
			...emailData,
		});
		if (!emailData) {
			return;
		}

		const { attachments, formattedBody } =
			await methods.prepareAttachments(emailData);

		const data = {
			subject: emailData.subject,
			body: formattedBody,
			cc: emailData.cc,
			bcc: emailData.bcc,
			to: emailData.to,
			attachments: attachments.filter((x) => !x.id || x.toRemove),
			sendType: SendType.Send,
		};

		const interactionId = callSession?.id ?? singleSendInteractionId;

		if (!interactionId) {
			showWorkspaceMessage("Interaction.EmailEditor.ErrorEmailSent", "error");

			return;
		}

		singleSend(interactionId, data)
			.then(() => {
				setEmailData(undefined);
				setSingleSendInteractionId(undefined);
				showWorkspaceMessage("Interaction.EmailEditor.SuccessEmailSent", "success");
			})
			.catch((ex: Error) => {
				captureError(ex, "Error while sending email", {
					interactionId,
				});
				console.error("Error while sending the email: ", ex);
				showWorkspaceMessage("Interaction.EmailEditor.ErrorEmailSent", "error");
			});
	}, [emailData, singleSendInteractionId, callSession?.id, showWorkspaceMessage]);

	const { data: languages = [] } = useQuery({
		queryKey: [TranscriptionLanguagesKey],
		queryFn: fetchTranscriptionLanguages,
		staleTime: Infinity,
	});

	const getLanguageText = (key: string | undefined): string | undefined => {
		return languages.find((lang) => lang.localeCode === key)?.displayName ?? key;
	};

	const updateTranscriptionMutation = useMutation({
		mutationFn: updateTranscriptionCall,
		onError: (error: any, data: any, _context: any) => {
			captureError(error, "Error starting transcription call", undefined, data);
			console.error(error);
		},
	});

	const updateTranscriptionAction = useCallback(
		(value: string | undefined) => {
			if (!value) {
				return;
			}

			addBreadcrumb(
				"ui_interaction",
				"Update transcription language",
				callSession
			);
			setSelectedLanguageKey(value);
			// Keep the shared session in sync so a later (re)start reuses this
			// selection; the update endpoint does not push a session notification.
			if (callSession) {
				callSession.transcriptionLanguage = value;
			}
			updateTranscriptionMutation.mutate(value);
		},
		[callSession, updateTranscriptionMutation]
	);

	const transcriptionContent = useMemo<JSX.Element>(() => {
		if (
			!callSession ||
			!callSession.callId ||
			!transcriptionStarted ||
			callSession.platformType !== PlatformType.Acs ||
			callSession.state === AgentInteractionStatus.Terminated
		) {
			return <></>;
		}

		return (
			<div
				className={`${classes.transcriptionChild} ${emailData ? classes.maxHeight50 : ""}`}
			>
				<Flex className={classes.header}>
					<Text
						size={500}
						children={intl.formatMessage({
							id: "Interaction.TranscriptionTitle",
						})}
					/>
					<div style={{ marginLeft: "auto", zIndex: 2 }}>
						<Combobox
							appearance="underline"
							value={getLanguageText(selectedLanguageKey)}
							selectedOptions={selectedLanguageKey ? [selectedLanguageKey] : []}
							onOptionSelect={(_, data) =>
								updateTranscriptionAction(data.optionValue)
							}
							freeform
							inlinePopup
						>
							{languages.map((lang) => (
								<Option
									key={lang.localeCode}
									value={lang.localeCode}
								>
									{lang.displayName}
								</Option>
							))}
						</Combobox>
					</div>
				</Flex>
				<div style={{ flexShrink: 0 }}>
					<Divider />
				</div>
				<MessagingChatInteractionContent
					key={callSession.callId}
					interactionId={callSession.callId}
					type={InteractionType.Call}
					isVariableTextOpen={false}
					hideSendBox={true}
					date={callSession.startDate}
				/>
			</div>
		);
	}, [callSession, emailData, transcriptionStarted, selectedLanguageKey, languages]);

	return (
		<>
			{(emailData || callSession?.transcriptionStarted) && (
				<Flex column hAlign="center" vAlign="center" className={classes.root}>
					{emailData && (
						<div className={classes.child}>
							<Flex className={classes.header}>
								<Text size={500} children={emailData.subject} />
							</Flex>
							<FluentProvider
								theme={teamsLightTheme}
								className={classes.themeProvider}
								id={HtmlIds.EditorFluentProvider}
							>
								<SingleSendEmailEditor
									data={emailData}
									onChange={(mailData?: EmailData) => {
										setEmailData(mailData);
									}}
									onSendClick={onSendEmailClick}
									closeEditor={() => setEmailData(undefined)}
									isSubjectEditingEnabled={true}
									isFirstEmailInConversation={true}
								/>
							</FluentProvider>
						</div>
					)}
					{transcriptionContent}
				</Flex>
			)}
		</>
	);
};
